"use client";

import { Suspense } from "react";

import { useSearchParams } from "next/navigation";

import { z } from "zod";

import { FormInput } from "@/components/forms/components/FormInputs";
import { PageFormTemplate } from "@/components/forms/components/PageFormTemplate";
import { PageViewTracker } from "@/components/observability/PageViewTracker";
import { handleResendVerificationAction } from "@/lib/actions/resend-verification";
import { EventKeys } from "@/lib/observability/catalog";
import { routes } from "@/lib/routes";

// Validation schema
const resendVerificationSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
});

function ResendVerificationContent() {
  const searchParams = useSearchParams();
  const prefilledEmail = (searchParams.get("email") ?? "").trim();
  const hasPrefilledEmail = prefilledEmail.length > 0;

  return (
    <div className="bg-default-50">
      <div className="w-full max-w-lg mx-auto py-16 px-4 sm:px-0">
        <PageFormTemplate
          callbackUrl={routes.auth.login()}
          defaultValues={{ email: prefilledEmail }}
          description={
            hasPrefilledEmail
              ? "We'll send a new verification link to the email below."
              : "Enter your email address to receive a new verification link"
          }
          eventKeys={{
            attempt: EventKeys.PUBLIC_RESEND_VERIFICATION_FORM_SUBMIT_ATTEMPT,
            failure: EventKeys.PUBLIC_RESEND_VERIFICATION_FORM_SUBMIT_FAILURE,
            success: EventKeys.PUBLIC_RESEND_VERIFICATION_FORM_SUBMIT_SUCCESS,
          }}
          formSchema={resendVerificationSchema}
          icon="Mail"
          submitLabel={
            hasPrefilledEmail
              ? "Send Verification Email Again"
              : "Send Verification Email"
          }
          title="Resend Verification Email"
          onSubmit={handleResendVerificationAction}
        >
          {({ control }) => (
            <>
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">📧</div>
              </div>

              <FormInput
                control={control}
                label="Email Address"
                name="email"
                placeholder="Enter your email address"
                type="email"
              />

              {hasPrefilledEmail && (
                <p className="text-sm text-default-600">
                  You can submit directly or edit this email first.
                </p>
              )}

              <div className="text-center space-y-2 mt-6">
                <p className="text-sm text-default-500">
                  Already verified your email?
                </p>
              </div>
            </>
          )}
        </PageFormTemplate>
      </div>
    </div>
  );
}

export default function ResendVerificationPage() {
  return (
    <>
      <PageViewTracker
        data={{ section: "resend-verification" }}
        eventKey={EventKeys.PUBLIC_RESEND_VERIFICATION_PAGE_VIEW}
      />
      <Suspense
        fallback={
          <div className="bg-default-50">
            <div className="w-full max-w-lg mx-auto py-16 px-4 sm:px-0">
              <div className="p-6 bg-white rounded-lg shadow-sm border border-default-200 max-w-md w-full mx-auto">
                <div className="text-center">Loading...</div>
              </div>
            </div>
          </div>
        }
      >
        <ResendVerificationContent />
      </Suspense>
    </>
  );
}
